/**
 * Declarative texture specifications.
 *
 * No third-party art ships with this project. Every texture is described here
 * as a small procedural recipe and rasterised by `tools/gen-assets.mjs` into a
 * 16x16 layer of a WebGL2 texture array. The array index of a texture is its
 * index in `TEXTURES`, so this file is the single source of truth shared by the
 * generator and the renderer.
 */

export type TexKind =
  | 'solid'
  | 'noise'
  | 'grain'
  | 'rings'
  | 'leaves'
  | 'ore'
  | 'bricks'
  | 'planks'
  | 'glass'
  | 'cross'
  | 'flower'
  | 'panel'
  | 'grid'
  | 'furnace'
  | 'checker'
  | 'liquid'
  | 'gradient'
  | 'crop'
  | 'spots'
  | 'crystal'
  | 'rail'
  | 'circle'
  | 'dust'
  | 'ladder'
  | 'top_grass'
  | 'portal'
  | 'item';

export interface TexSpec {
  name: string;
  kind: TexKind;
  /** Primary colours, as 0xRRGGBB. Meaning depends on `kind`. */
  colors: number[];
  /** Amount of per-pixel value jitter, 0..1. */
  jitter?: number;
  /** Kind-specific tuning. */
  p?: Record<string, number>;
  /** Texture has meaningful alpha (affects mesher's opacity handling). */
  alpha?: boolean;
  /** Multiply by the biome grass/foliage tint at render time. */
  tint?: 'grass' | 'foliage' | 'water' | 'redstone';
}

function t(
  name: string,
  kind: TexKind,
  colors: number[],
  extra: Partial<TexSpec> = {},
): TexSpec {
  return { name, kind, colors, ...extra };
}

export const TEXTURES: readonly TexSpec[] = [
  // ---- stone family -------------------------------------------------------
  t('stone', 'noise', [0x7d7d7d], { jitter: 0.14, p: { scale: 1 } }),
  t('cobblestone', 'spots', [0x7a7a7a, 0x5f5f5f, 0x9a9a9a], { jitter: 0.1, p: { cells: 4 } }),
  t('mossy_cobblestone', 'spots', [0x6f7a63, 0x53603f, 0x8a9a7a], { jitter: 0.1, p: { cells: 4 } }),
  t('smooth_stone', 'noise', [0x9d9d9d], { jitter: 0.05 }),
  t('stone_bricks', 'bricks', [0x7a7a7a, 0x646464], { jitter: 0.07, p: { rows: 4, cols: 2 } }),
  t('cracked_stone_bricks', 'bricks', [0x6f6f6f, 0x555555], {
    jitter: 0.14,
    p: { rows: 4, cols: 2, cracks: 1 },
  }),
  t('mossy_stone_bricks', 'bricks', [0x6d7566, 0x545c46], { jitter: 0.1, p: { rows: 4, cols: 2 } }),
  t('chiseled_stone_bricks', 'panel', [0x757575, 0x5c5c5c, 0x8a8a8a]),
  t('granite', 'noise', [0x9a6a5a], { jitter: 0.14 }),
  t('diorite', 'spots', [0xbfbfbf, 0x9d9d9d, 0xdcdcdc], { jitter: 0.1, p: { cells: 5 } }),
  t('andesite', 'noise', [0x898c8c], { jitter: 0.12 }),
  t('deepslate', 'grain', [0x4c4c50, 0x3c3c40], { jitter: 0.1, p: { vertical: 1, lines: 5 } }),
  t('deepslate_top', 'noise', [0x4f4f54], { jitter: 0.12 }),
  t('cobbled_deepslate', 'spots', [0x4a4a4e, 0x38383c, 0x5e5e64], { jitter: 0.1, p: { cells: 4 } }),
  t('tuff', 'noise', [0x6c6f62], { jitter: 0.16 }),
  t('calcite', 'noise', [0xdfdfd6], { jitter: 0.05 }),
  t('bedrock', 'spots', [0x565656, 0x2a2a2a, 0x7c7c7c], { jitter: 0.22, p: { cells: 6 } }),
  t('obsidian', 'spots', [0x14101f, 0x0d0a16, 0x2a2140], { jitter: 0.12, p: { cells: 5 } }),
  t('crying_obsidian', 'spots', [0x1a1030, 0x0d0a16, 0x6a30d0], { jitter: 0.14, p: { cells: 5 } }),

  // ---- dirt / ground ------------------------------------------------------
  t('dirt', 'noise', [0x866043], { jitter: 0.16 }),
  t('coarse_dirt', 'noise', [0x7b573b], { jitter: 0.22 }),
  t('rooted_dirt', 'noise', [0x906c4c], { jitter: 0.18 }),
  t('grass_block_top', 'top_grass', [0x91bd59], { jitter: 0.13, tint: 'grass' }),
  t('grass_block_side', 'gradient', [0x866043, 0x91bd59], { jitter: 0.15, p: { depth: 5 } }),
  t('grass_block_side_overlay', 'gradient', [0x91bd59, 0x91bd59], {
    jitter: 0.13,
    p: { depth: 5, overlay: 1 },
    alpha: true,
    tint: 'grass',
  }),
  t('podzol_top', 'noise', [0x6b4d2a], { jitter: 0.2 }),
  t('mycelium_top', 'spots', [0x6f6265, 0x5b4f52, 0x8a7a80], { jitter: 0.14, p: { cells: 6 } }),
  t('sand', 'noise', [0xdbd3a0], { jitter: 0.09 }),
  t('red_sand', 'noise', [0xbe6720], { jitter: 0.09 }),
  t('gravel', 'spots', [0x8a8685, 0x6b6866, 0xa9a5a3], { jitter: 0.15, p: { cells: 6 } }),
  t('clay', 'noise', [0xa4a8b8], { jitter: 0.07 }),
  t('sandstone_top', 'noise', [0xd9d0a5], { jitter: 0.06 }),
  t('sandstone', 'grain', [0xd7ceA0, 0xc6bc8d], { jitter: 0.07, p: { lines: 4 } }),
  t('red_sandstone', 'grain', [0xba6b28, 0xa55c20], { jitter: 0.07, p: { lines: 4 } }),
  t('snow', 'noise', [0xf2fafa], { jitter: 0.03 }),
  t('ice', 'glass', [0x7dadff, 0xa8c9ff], { jitter: 0.06, p: { solid: 1 } }),
  t('packed_ice', 'noise', [0x8ab6f0], { jitter: 0.06 }),
  t('mud', 'noise', [0x3c3a3d], { jitter: 0.14 }),
  t('moss_block', 'noise', [0x5a7024], { jitter: 0.18 }),

  // ---- ores ---------------------------------------------------------------
  t('coal_ore', 'ore', [0x7d7d7d, 0x1d1d1d], { jitter: 0.12 }),
  t('iron_ore', 'ore', [0x7d7d7d, 0xd8a882], { jitter: 0.12 }),
  t('copper_ore', 'ore', [0x7d7d7d, 0xd88b5a], { jitter: 0.12 }),
  t('gold_ore', 'ore', [0x7d7d7d, 0xfcee4b], { jitter: 0.12 }),
  t('redstone_ore', 'ore', [0x7d7d7d, 0xd42f2f], { jitter: 0.12 }),
  t('lapis_ore', 'ore', [0x7d7d7d, 0x2f52d4], { jitter: 0.12 }),
  t('diamond_ore', 'ore', [0x7d7d7d, 0x5decd5], { jitter: 0.12 }),
  t('emerald_ore', 'ore', [0x7d7d7d, 0x28d955], { jitter: 0.12 }),
  t('deepslate_coal_ore', 'ore', [0x4f4f54, 0x141414], { jitter: 0.12 }),
  t('deepslate_iron_ore', 'ore', [0x4f4f54, 0xc99b76], { jitter: 0.12 }),
  t('deepslate_copper_ore', 'ore', [0x4f4f54, 0xc87c4d], { jitter: 0.12 }),
  t('deepslate_gold_ore', 'ore', [0x4f4f54, 0xecdd40], { jitter: 0.12 }),
  t('deepslate_redstone_ore', 'ore', [0x4f4f54, 0xc42525], { jitter: 0.12 }),
  t('deepslate_lapis_ore', 'ore', [0x4f4f54, 0x2848c4], { jitter: 0.12 }),
  t('deepslate_diamond_ore', 'ore', [0x4f4f54, 0x4bd8c2], { jitter: 0.12 }),
  t('deepslate_emerald_ore', 'ore', [0x4f4f54, 0x22c44a], { jitter: 0.12 }),
  t('nether_quartz_ore', 'ore', [0x703634, 0xe8e0d8], { jitter: 0.12 }),
  t('nether_gold_ore', 'ore', [0x703634, 0xfcee4b], { jitter: 0.12 }),
  t('ancient_debris_top', 'ore', [0x5c4038, 0x2a2020], { jitter: 0.14 }),
  t('ancient_debris_side', 'ore', [0x5c4038, 0x3a2c26], { jitter: 0.14 }),

  // ---- wood ---------------------------------------------------------------
  t('oak_log', 'grain', [0x9c7f4e, 0x6b5535], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('oak_log_top', 'rings', [0xb08a51, 0x8a6c3e]),
  t('spruce_log', 'grain', [0x6b4f2f, 0x4a361f], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('spruce_log_top', 'rings', [0x876b45, 0x5f4a2d]),
  t('birch_log', 'grain', [0xd7cfc0, 0x5a5347], { jitter: 0.05, p: { vertical: 1, lines: 3 } }),
  t('birch_log_top', 'rings', [0xc8b98e, 0xa2946d]),
  t('jungle_log', 'grain', [0x9a7048, 0x5c4028], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('jungle_log_top', 'rings', [0xa8804f, 0x7a5c38]),
  t('acacia_log', 'grain', [0x8a6244, 0x5d4230], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('acacia_log_top', 'rings', [0xb06b3f, 0x8a5330]),
  t('dark_oak_log', 'grain', [0x4a3520, 0x2f2214], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('dark_oak_log_top', 'rings', [0x54402a, 0x3a2c1c]),
  t('oak_planks', 'planks', [0xb08a51, 0x8f6f3f], { jitter: 0.07 }),
  t('spruce_planks', 'planks', [0x7a5b36, 0x604528], { jitter: 0.07 }),
  t('birch_planks', 'planks', [0xd7c185, 0xb5a06a], { jitter: 0.07 }),
  t('jungle_planks', 'planks', [0xa9765a, 0x8a5d45], { jitter: 0.07 }),
  t('acacia_planks', 'planks', [0xba6337, 0x9a4f2a], { jitter: 0.07 }),
  t('dark_oak_planks', 'planks', [0x4a301a, 0x372414], { jitter: 0.07 }),
  t('crimson_planks', 'planks', [0x6a3040, 0x522432], { jitter: 0.07 }),
  t('warped_planks', 'planks', [0x2b6b62, 0x1f524b], { jitter: 0.07 }),
  t('crimson_stem', 'grain', [0x6a3040, 0x4a2030], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('crimson_stem_top', 'rings', [0x8a4a3a, 0x6a3428]),
  t('warped_stem', 'grain', [0x2b6b62, 0x1a4a44], { jitter: 0.08, p: { vertical: 1, lines: 5 } }),
  t('warped_stem_top', 'rings', [0x3a8a7a, 0x286058]),
  t('oak_leaves', 'leaves', [0x5aa02a], { jitter: 0.2, alpha: true, tint: 'foliage' }),
  t('spruce_leaves', 'leaves', [0x4a7a4a], { jitter: 0.2, alpha: true }),
  t('birch_leaves', 'leaves', [0x82b25a], { jitter: 0.2, alpha: true }),
  t('jungle_leaves', 'leaves', [0x3fa02a], { jitter: 0.2, alpha: true, tint: 'foliage' }),
  t('acacia_leaves', 'leaves', [0x86a83a], { jitter: 0.2, alpha: true, tint: 'foliage' }),
  t('dark_oak_leaves', 'leaves', [0x3f7a20], { jitter: 0.2, alpha: true, tint: 'foliage' }),
  t('nether_wart_block', 'noise', [0x731a09], { jitter: 0.16 }),
  t('warped_wart_block', 'noise', [0x167e86], { jitter: 0.16 }),
  t('shroomlight', 'noise', [0xf19a4a], { jitter: 0.14 }),

  // ---- nether / end -------------------------------------------------------
  t('netherrack', 'noise', [0x703634], { jitter: 0.2 }),
  t('soul_sand', 'spots', [0x54413a, 0x3a2c26, 0x6a544a], { jitter: 0.14, p: { cells: 5 } }),
  t('soul_soil', 'noise', [0x4b3a32], { jitter: 0.16 }),
  t('nether_bricks', 'bricks', [0x2e161a, 0x1c0d10], { jitter: 0.08, p: { rows: 4, cols: 2 } }),
  t('red_nether_bricks', 'bricks', [0x6a0a0a, 0x3d0606], { jitter: 0.08, p: { rows: 4, cols: 2 } }),
  t('glowstone', 'spots', [0xf7c96a, 0xc99a3a, 0xffe9a8], { jitter: 0.12, p: { cells: 5 } }),
  t('magma_block', 'spots', [0x8e3b1c, 0x50190c, 0xf2a03a], { jitter: 0.16, p: { cells: 4 } }),
  t('basalt_side', 'grain', [0x4c4a52, 0x38363e], { jitter: 0.1, p: { vertical: 1, lines: 6 } }),
  t('basalt_top', 'noise', [0x4f4d55], { jitter: 0.12 }),
  t('blackstone', 'noise', [0x2b2530], { jitter: 0.14 }),
  t('quartz_block', 'noise', [0xece5dd], { jitter: 0.05 }),
  t('end_stone', 'noise', [0xdcdca8], { jitter: 0.1 }),
  t('end_stone_bricks', 'bricks', [0xdadfa8, 0xc2c78e], { jitter: 0.06, p: { rows: 4, cols: 2 } }),
  t('purpur_block', 'noise', [0xa87ba8], { jitter: 0.09 }),
  t('purpur_pillar', 'grain', [0xa87ba8, 0x8f628f], { jitter: 0.07, p: { vertical: 1, lines: 3 } }),
  t('purpur_pillar_top', 'rings', [0xb18ab1, 0x96709a]),
  t('end_portal_frame_top', 'panel', [0x3a7a6a, 0x2a5a4c, 0x5aa08a]),
  t('end_portal_frame_side', 'grain', [0x4a7a6c, 0x36584e], { jitter: 0.08, p: { lines: 3 } }),
  t('end_portal', 'portal', [0x0a0018, 0x3a1a6a, 0xc0a0ff]),
  t('nether_portal', 'portal', [0x1a0430, 0x6a1acc, 0xd0a0ff], { alpha: true }),
  t('end_rod', 'panel', [0xe8e0d8, 0xb8b0a8, 0xffffff]),
  t('dragon_egg', 'spots', [0x120c1c, 0x060409, 0x2a1a3a], { jitter: 0.14, p: { cells: 6 } }),

  // ---- glass / decorative -------------------------------------------------
  t('glass', 'glass', [0xd8f0f8, 0xffffff], { alpha: true }),
  t('bricks', 'bricks', [0x985542, 0xb0b0b0], { jitter: 0.07, p: { rows: 4, cols: 2 } }),
  t('bookshelf', 'panel', [0xb08a51, 0x6a4a2a, 0xc8a86a], { p: { books: 1 } }),
  t('crafting_table_top', 'grid', [0x8a6a3a, 0x5a4020, 0xb08a51]),
  t('crafting_table_side', 'panel', [0xb08a51, 0x7a5a30, 0x8a6a3a], { p: { tools: 1 } }),
  t('crafting_table_front', 'panel', [0xb08a51, 0x7a5a30, 0x6a4a20], { p: { tools: 1 } }),
  t('furnace_top', 'noise', [0x767676], { jitter: 0.08 }),
  t('furnace_side', 'noise', [0x6e6e6e], { jitter: 0.1 }),
  t('furnace_front', 'furnace', [0x6e6e6e, 0x2a2a2a, 0x9a9a9a]),
  t('furnace_front_on', 'furnace', [0x6e6e6e, 0xf2a03a, 0x9a9a9a], { p: { lit: 1 } }),
  t('chest_top', 'panel', [0x9a6f30, 0x6a4a1a, 0xb88a44]),
  t('chest_side', 'panel', [0x9a6f30, 0x6a4a1a, 0xb88a44], { p: { chest: 1 } }),
  t('chest_front', 'panel', [0x9a6f30, 0x6a4a1a, 0x4a4a4a], { p: { chest: 1, latch: 1 } }),
  t('barrel_top', 'circle', [0x8a6a3a, 0x5a4020, 0xa88a4a]),
  t('barrel_side', 'planks', [0x8a6a3a, 0x6a4a20], { jitter: 0.07, p: { hoops: 1 } }),
  t('tnt_side', 'panel', [0xd83c2c, 0xf0f0f0, 0x9a2a1a], { p: { tnt: 1 } }),
  t('tnt_top', 'noise', [0xd83c2c], { jitter: 0.08 }),
  t('tnt_bottom', 'noise', [0x6a4a2a], { jitter: 0.08 }),
  t('sponge', 'noise', [0xc7c743], { jitter: 0.2 }),
  t('hay_side', 'grain', [0xc9a52b, 0xa8871f], { jitter: 0.1, p: { lines: 6 } }),
  t('hay_top', 'circle', [0xd9b53b, 0x8a6f18, 0xe8c85a]),
  t('slime_block', 'glass', [0x7ac07a, 0x9fe09f], { alpha: true, p: { solid: 1 } }),
  t('honey_block', 'glass', [0xe8a52c, 0xffd070], { alpha: true, p: { solid: 1 } }),
  t('spawner', 'grid', [0x1a2a3a, 0x0d1520, 0x2f4a60]),
  t('cobweb', 'cross', [0xf0f0f0], { alpha: true, p: { web: 1 } }),
  t('ladder', 'ladder', [0x9a7440, 0x6a4a20], { alpha: true }),
  t('scaffold', 'grid', [0xc8a86a, 0x9a7440, 0xd8bc86]),
  t('terracotta', 'noise', [0x985e43], { jitter: 0.1 }),
  t('bell', 'panel', [0xf2c33c, 0xa8801a, 0xffe07a]),
  t('lantern', 'panel', [0x6a6a6a, 0xf2c33c, 0x3a3a3a], { p: { lantern: 1 } }),
  t('torch', 'cross', [0x8a6a3a, 0xffdd66], { alpha: true, p: { torch: 1 } }),
  t('soul_torch', 'cross', [0x8a6a3a, 0x66ddff], { alpha: true, p: { torch: 1 } }),
  t('campfire', 'furnace', [0x6a4a2a, 0xf2a03a, 0x8a6a3a], { p: { lit: 1 } }),

  // ---- wool (16 colours) --------------------------------------------------
  t('white_wool', 'noise', [0xe9ecec], { jitter: 0.06 }),
  t('orange_wool', 'noise', [0xf07613], { jitter: 0.06 }),
  t('magenta_wool', 'noise', [0xbd44b3], { jitter: 0.06 }),
  t('light_blue_wool', 'noise', [0x3aafd9], { jitter: 0.06 }),
  t('yellow_wool', 'noise', [0xf8c627], { jitter: 0.06 }),
  t('lime_wool', 'noise', [0x70b919], { jitter: 0.06 }),
  t('pink_wool', 'noise', [0xed8dac], { jitter: 0.06 }),
  t('gray_wool', 'noise', [0x3e4447], { jitter: 0.06 }),
  t('light_gray_wool', 'noise', [0x8e8e86], { jitter: 0.06 }),
  t('cyan_wool', 'noise', [0x158991], { jitter: 0.06 }),
  t('purple_wool', 'noise', [0x792aac], { jitter: 0.06 }),
  t('blue_wool', 'noise', [0x35399d], { jitter: 0.06 }),
  t('brown_wool', 'noise', [0x724728], { jitter: 0.06 }),
  t('green_wool', 'noise', [0x546d1b], { jitter: 0.06 }),
  t('red_wool', 'noise', [0xa12722], { jitter: 0.06 }),
  t('black_wool', 'noise', [0x141519], { jitter: 0.06 }),

  // ---- liquids ------------------------------------------------------------
  t('water_still', 'liquid', [0x3f76e4, 0x2f5fd0], { alpha: true, tint: 'water' }),
  t('water_flow', 'liquid', [0x3f76e4, 0x2f5fd0], { alpha: true, p: { flow: 1 }, tint: 'water' }),
  t('lava_still', 'liquid', [0xd45a12, 0xf2b13a], { p: { hot: 1 } }),
  t('lava_flow', 'liquid', [0xd45a12, 0xf2b13a], { p: { hot: 1, flow: 1 } }),

  // ---- plants -------------------------------------------------------------
  t('short_grass', 'cross', [0x6aa03a], { alpha: true, tint: 'grass', p: { blades: 1 } }),
  t('tall_grass_top', 'cross', [0x6aa03a], { alpha: true, tint: 'grass', p: { blades: 1, top: 1 } }),
  t('tall_grass_bottom', 'cross', [0x6aa03a], { alpha: true, tint: 'grass', p: { blades: 1 } }),
  t('fern', 'cross', [0x5a9a3a], { alpha: true, tint: 'grass', p: { fern: 1 } }),
  t('dead_bush', 'cross', [0x9a6a2a], { alpha: true, p: { bush: 1 } }),
  t('dandelion', 'flower', [0x5a9a2a, 0xf8e14a], { alpha: true }),
  t('poppy', 'flower', [0x5a9a2a, 0xd52a2a], { alpha: true }),
  t('blue_orchid', 'flower', [0x5a9a2a, 0x30a0e0], { alpha: true }),
  t('allium', 'flower', [0x5a9a2a, 0xb066d0], { alpha: true }),
  t('cornflower', 'flower', [0x5a9a2a, 0x4a6ad0], { alpha: true }),
  t('oxeye_daisy', 'flower', [0x5a9a2a, 0xf0f0e0], { alpha: true }),
  t('brown_mushroom', 'flower', [0xa08060, 0x9a6a3a], { alpha: true, p: { mushroom: 1 } }),
  t('red_mushroom', 'flower', [0xe0e0e0, 0xd02a2a], { alpha: true, p: { mushroom: 1 } }),
  t('sugar_cane', 'cross', [0x94c56a], { alpha: true, p: { cane: 1 }, tint: 'foliage' }),
  t('cactus_side', 'noise', [0x4a7a2a], { jitter: 0.1, p: { cactus: 1 } }),
  t('cactus_top', 'circle', [0x59853a, 0x3a5a1a, 0x6a9a4a]),
  t('vine', 'leaves', [0x4a8a2a], { jitter: 0.2, alpha: true, tint: 'foliage' }),
  t('lily_pad', 'circle', [0x2a7a2a, 0x1a5a1a, 0x3a9a3a], { alpha: true }),
  t('pumpkin_top', 'circle', [0xc07615, 0x8a4f0a, 0xd88a2a]),
  t('pumpkin_side', 'grain', [0xc07615, 0x9a5a10], { jitter: 0.06, p: { vertical: 1, lines: 6 } }),
  t('pumpkin_face', 'panel', [0xc07615, 0x9a5a10, 0x2a1a0a], { p: { face: 1 } }),
  t('melon_top', 'noise', [0x6a9a2a], { jitter: 0.1 }),
  t('melon_side', 'grain', [0x6f962c, 0x4f7a1c], { jitter: 0.1, p: { vertical: 1, lines: 5 } }),
  t('wheat_stage0', 'crop', [0x8aa050], { alpha: true, p: { stage: 0 } }),
  t('wheat_stage3', 'crop', [0x9aa840], { alpha: true, p: { stage: 3 } }),
  t('wheat_stage7', 'crop', [0xdcbb65], { alpha: true, p: { stage: 7 } }),
  t('carrots_stage0', 'crop', [0x2a7a2a], { alpha: true, p: { stage: 0 } }),
  t('carrots_stage3', 'crop', [0x2f8a2f], { alpha: true, p: { stage: 3 } }),
  t('carrots_stage7', 'crop', [0x3a9a3a], { alpha: true, p: { stage: 7, berries: 0xf08020 } }),
  t('potatoes_stage0', 'crop', [0x3a8a2a], { alpha: true, p: { stage: 0 } }),
  t('potatoes_stage3', 'crop', [0x429a32], { alpha: true, p: { stage: 3 } }),
  t('potatoes_stage7', 'crop', [0x4aaa3a], { alpha: true, p: { stage: 7, berries: 0xd8c060 } }),
  t('nether_wart_stage0', 'crop', [0x8a1a1a], { alpha: true, p: { stage: 0 } }),
  t('nether_wart_stage2', 'crop', [0xa82020], { alpha: true, p: { stage: 7 } }),
  t('farmland', 'noise', [0x5a3a1a], { jitter: 0.14 }),
  t('farmland_moist', 'noise', [0x3f2810], { jitter: 0.14 }),
  t('chorus_plant', 'noise', [0x7a5a7a], { jitter: 0.14 }),

  // ---- redstone -----------------------------------------------------------
  t('redstone_dust_line', 'dust', [0xff0000], { alpha: true, tint: 'redstone', p: { line: 1 } }),
  t('redstone_dust_dot', 'dust', [0xff0000], { alpha: true, tint: 'redstone', p: { dot: 1 } }),
  t('redstone_block', 'spots', [0xa81a1a, 0x8a1010, 0xd83030], { jitter: 0.12, p: { cells: 5 } }),
  t('redstone_lamp', 'noise', [0x674d3a], { jitter: 0.1 }),
  t('redstone_lamp_on', 'spots', [0xd8a44a, 0xb08030, 0xffd88a], { jitter: 0.1, p: { cells: 4 } }),
  t('redstone_torch', 'cross', [0x8a6a3a, 0xff2a2a], { alpha: true, p: { torch: 1 } }),
  t('redstone_torch_off', 'cross', [0x8a6a3a, 0x7a1a1a], { alpha: true, p: { torch: 1 } }),
  t('repeater_top', 'panel', [0xa8a8a8, 0x8a8a8a, 0x7a1a1a], { p: { repeater: 1 } }),
  t('repeater_top_on', 'panel', [0xa8a8a8, 0x8a8a8a, 0xff2a2a], { p: { repeater: 1, lit: 1 } }),
  t('comparator_top', 'panel', [0xa8a8a8, 0x8a8a8a, 0x7a1a1a], { p: { comparator: 1 } }),
  t('comparator_top_on', 'panel', [0xa8a8a8, 0x8a8a8a, 0xff2a2a], { p: { comparator: 1, lit: 1 } }),
  t('piston_top', 'planks', [0xb8a878, 0x8a7a50], { jitter: 0.06 }),
  t('piston_side', 'panel', [0x9a9a9a, 0x6a6a6a, 0xb8a878], { p: { piston: 1 } }),
  t('piston_bottom', 'noise', [0x8a8a8a], { jitter: 0.09 }),
  t('piston_inner', 'noise', [0x757575], { jitter: 0.09 }),
  t('sticky_piston_top', 'noise', [0x8aa858], { jitter: 0.1 }),
  t('observer_top', 'panel', [0x6a6a6a, 0x4a4a4a, 0x9a9a9a], { p: { observer: 1 } }),
  t('observer_front', 'panel', [0x6a6a6a, 0x3a3a3a, 0x8a8a8a], { p: { observerFace: 1 } }),
  t('observer_back', 'panel', [0x6a6a6a, 0x3a3a3a, 0xd82a2a], { p: { observerBack: 1 } }),
  t('observer_side', 'grain', [0x6a6a6a, 0x585858], { jitter: 0.07, p: { lines: 4 } }),
  t('hopper_top', 'grid', [0x4a4a4a, 0x2a2a2a, 0x6a6a6a]),
  t('hopper_side', 'panel', [0x4a4a4a, 0x2a2a2a, 0x6a6a6a], { p: { hopper: 1 } }),
  t('hopper_inside', 'noise', [0x3a3a3a], { jitter: 0.08 }),
  t('dispenser_front', 'furnace', [0x6e6e6e, 0x1a1a1a, 0x9a9a9a]),
  t('dropper_front', 'furnace', [0x6e6e6e, 0x2a2a2a, 0x9a9a9a]),
  t('lever', 'cross', [0x8a8a8a, 0x9a7440], { alpha: true, p: { lever: 1 } }),
  t('note_block', 'panel', [0x6a4a2a, 0x4a3018, 0x8a6a3a], { p: { note: 1 } }),
  t('daylight_detector_top', 'glass', [0x4a6a8a, 0x8ac0e0], { p: { solid: 1 } }),
  t('daylight_detector_side', 'planks', [0x9a7a50, 0x7a5a34], { jitter: 0.06 }),
  t('target', 'circle', [0xf0e0d0, 0xd82a2a, 0xffffff]),
  t('rail', 'rail', [0x9a8a6a, 0x6a5a3a], { alpha: true }),
  t('powered_rail', 'rail', [0xd8a02a, 0x8a6a1a], { alpha: true }),
  t('powered_rail_on', 'rail', [0xf8c84a, 0xd82a2a], { alpha: true }),
  t('detector_rail', 'rail', [0x9a8a6a, 0xd82a2a], { alpha: true }),
  t('activator_rail', 'rail', [0x6a5a4a, 0x3a3a3a], { alpha: true }),
  t('activator_rail_on', 'rail', [0x8a6a5a, 0xd82a2a], { alpha: true }),

  // ---- metal / mineral blocks --------------------------------------------
  t('iron_block', 'noise', [0xdcdcdc], { jitter: 0.05 }),
  t('gold_block', 'noise', [0xf8d84a], { jitter: 0.05 }),
  t('diamond_block', 'crystal', [0x5decd5, 0x2ab0a0, 0xa8fff0]),
  t('emerald_block', 'crystal', [0x28d955, 0x18a03a, 0x8affa0]),
  t('lapis_block', 'spots', [0x2f52d4, 0x1f3aa0, 0x5a80f0], { jitter: 0.1, p: { cells: 5 } }),
  t('coal_block', 'noise', [0x1a1a1a], { jitter: 0.12 }),
  t('copper_block', 'noise', [0xc06a3a], { jitter: 0.06 }),
  t('netherite_block', 'noise', [0x44404a], { jitter: 0.1 }),
];

/** Map from texture name to its layer index in the generated array texture. */
export const TEX_INDEX: ReadonlyMap<string, number> = new Map(
  TEXTURES.map((spec, i) => [spec.name, i]),
);

export function texId(name: string): number {
  const i = TEX_INDEX.get(name);
  if (i === undefined) throw new Error(`unknown texture: ${name}`);
  return i;
}

export const TEXTURE_SIZE = 16;
export const TEXTURE_COUNT = TEXTURES.length;

/** Texture layers whose colour is multiplied by a biome tint at render time. */
export const TINT_KIND = { none: 0, grass: 1, foliage: 2, water: 3, redstone: 4 } as const;

export const TEX_TINT: Uint8Array = (() => {
  const arr = new Uint8Array(TEXTURES.length);
  TEXTURES.forEach((spec, i) => {
    arr[i] = spec.tint ? TINT_KIND[spec.tint] : 0;
  });
  return arr;
})();
